import React from 'react';
import {DateUtil} from "@/utils/dateUtil.ts";
import {PrintUtil} from "@/utils/printUtil.ts";

interface PrescriptionContentProps {
  data: any;
  translations: any;
  language: string;
  clinicInfo: any;
  doctorInfo: any;
  patientInfo: any;
  settings: any;
  currentUser: any;
}

export function PrescriptionContent({
                                      data,
                                      translations: t,
                                      language,
                                      clinicInfo,
                                      doctorInfo,
                                      patientInfo,
                                      settings,
                                      currentUser,
                                    }: Readonly<PrescriptionContentProps>) {
  const invoiceDate = new Date().toISOString();

  const normalizeText = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  const hasText = (value: unknown): boolean => normalizeText(value).length > 0;

  const isZeroLike = (value: string): boolean => {
    const v = normalizeText(value).toLowerCase();
    return v === '' || v === '0' || v === '0.0' || v === '0.00';
  };

  const phraseMap: Record<string, string> = {
    'after breakfast': t.afterBreakfast ?? 'After Breakfast',
    'before breakfast': t.beforeBreakfast ?? 'Before Breakfast',
    'with breakfast': t.withBreakfast ?? 'With Breakfast',

    'after lunch': t.afterLunch ?? 'After Lunch',
    'before lunch': t.beforeLunch ?? 'Before Lunch',
    'with lunch': t.withLunch ?? 'With Lunch',

    'after dinner': t.afterDinner ?? 'After Dinner',
    'before dinner': t.beforeDinner ?? 'Before Dinner',
    'with dinner': t.withDinner ?? 'With Dinner',

    'after food': t.afterFood ?? 'After Food',
    'before food': t.beforeFood ?? 'Before Food',
    'with food': t.withFood ?? 'With Food',
    'empty stomach': t.emptyStomach ?? 'Empty Stomach',
    'at bedtime': t.atBedtime ?? 'At Bedtime',

    'morning': t.morning ?? 'Morning',
    'afternoon': t.afternoon ?? 'Afternoon',
    'evening': t.evening ?? 'Evening',
    'night': t.night ?? 'Night',

    'as directed': t.asDirected ?? 'As Directed',
    'sos': t.sos ?? 'SOS',
    'days': t.days ?? 'days',
    'day': t.day ?? 'day',
    'weeks': t.weeks ?? 'weeks',
    'week': t.week ?? 'week',
    'months': t.months ?? 'months',
    'month': t.month ?? 'month',
  };

  const translatePhrase = (text: string): string => {
    const input = normalizeText(text);
    if (!input) return '';

    let output = input.toLowerCase();
    const keys = Object.keys(phraseMap).sort((a, b) => b.length - a.length);

    for (const key of keys) {
      output = output.replace(new RegExp(key, 'gi'), phraseMap[key]);
    }

    return output;
  };

  const translateDuration = (duration: string): string => {
    const value = normalizeText(duration);
    if (!value) return '';
    return translatePhrase(value);
  };

  const getMealPhrase = (
      when: string,
      slot: 'morning' | 'afternoon' | 'night'
  ): string => {
    const normalizedWhen = normalizeText(when).toLowerCase();

    const mealMap: Record<string, Record<'morning' | 'afternoon' | 'night', string>> = {
      'after food': {
        morning: 'after breakfast',
        afternoon: 'after lunch',
        night: 'after dinner',
      },
      'before food': {
        morning: 'before breakfast',
        afternoon: 'before lunch',
        night: 'before dinner',
      },
      'with food': {
        morning: 'with breakfast',
        afternoon: 'with lunch',
        night: 'with dinner',
      },
      'empty stomach': {
        morning: 'empty stomach',
        afternoon: 'empty stomach',
        night: 'empty stomach',
      },
      'at bedtime': {
        morning: 'at bedtime',
        afternoon: 'at bedtime',
        night: 'at bedtime',
      },
    };

    return mealMap[normalizedWhen]?.[slot] ?? slot;
  };

  const formatDoseValue = (value: string): string => normalizeText(value);

  const calculateTimingString = (med: any): string => {

    console.log('Calculating timing string for medication:', med);

    const dosage = normalizeText(med?.dosage);
    const when = normalizeText(med?.taken_when ?? med?.when);
    const duration = normalizeText(med?.duration);


    if (!dosage) {
      return duration ? translateDuration(duration) : '';
    }

    const normalizedDosage = dosage.toLowerCase();

    if (normalizedDosage === 'sos') {
      return t.sos ?? 'SOS';
    }

    if (normalizedDosage === 'as directed') {
      return t.asDirected ?? 'As Directed';
    }

    if (normalizedDosage === 'hs' || normalizedDosage === 'bedtime') {
      const base = translatePhrase('at bedtime');
      return duration ? `${base} - ${translateDuration(duration)}` : base;
    }

    if (normalizedDosage === 'od') {
      const base = `1 ${getMealPhrase(when || 'after food', 'morning')}`;
      return duration ? `${translatePhrase(base)} - ${translateDuration(duration)}` : translatePhrase(base);
    }

    if (normalizedDosage === 'bd') {
      const base = [
        `1 ${getMealPhrase(when || 'after food', 'morning')}`,
        `1 ${getMealPhrase(when || 'after food', 'night')}`,
      ].join(', ');
      return duration ? `${translatePhrase(base)} - ${translateDuration(duration)}` : translatePhrase(base);
    }

    if (normalizedDosage === 'tds' || normalizedDosage === 'tid') {
      const base = [
        `1 ${getMealPhrase(when || 'after food', 'morning')}`,
        `1 ${getMealPhrase(when || 'after food', 'afternoon')}`,
        `1 ${getMealPhrase(when || 'after food', 'night')}`,
      ].join(', ');
      return duration ? `${translatePhrase(base)} - ${translateDuration(duration)}` : translatePhrase(base);
    }

    const parts = dosage.split('-').map((p: string) => normalizeText(p));

    if (parts.length !== 3) {
      const fallback = when ? `${dosage} ${when}` : dosage;
      return duration ? `${translatePhrase(fallback)} - ${translateDuration(duration)}` : translatePhrase(fallback);
    }

    const [morning, afternoon, night] = parts;
    const instructions: string[] = [];

    if (!isZeroLike(morning)) {
      instructions.push(`${formatDoseValue(morning)} ${getMealPhrase(when, 'morning')}`);
    }

    if (!isZeroLike(afternoon)) {
      instructions.push(`${formatDoseValue(afternoon)} ${getMealPhrase(when, 'afternoon')}`);
    }

    if (!isZeroLike(night)) {
      instructions.push(`${formatDoseValue(night)} ${getMealPhrase(when, 'night')}`);
    }

    let result = translatePhrase(instructions.join(', '));

    if (duration) {
      result = result ? `${result} - ${translateDuration(duration)}` : translateDuration(duration);
    }

    return result;
  };

  const buildDirectionsText = (med: any): string => {
    return calculateTimingString(med);
  };

  const medicineNameOf = (med: any): string => {
    return normalizeText(med?.product || med?.brand_name || med?.generic_name);
  };

  const buildDirectionsCell = (med: any): string => {
    const when = normalizeText(med?.taken_when ?? med?.when);
    const duration = normalizeText(med?.duration);

    const translatedWhen = when ? translatePhrase(when) : '';
    const translatedDuration = duration ? translateDuration(duration) : '';

    if (translatedWhen && translatedDuration) {
      return `${translatedWhen} - ${translatedDuration}`;
    }

    return translatedWhen || translatedDuration || '';
  };

  return (
      <>
        <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10mm',
              marginBottom: '8mm',
              fontSize: '11px',
              borderBottom: '1px solid #000'
            }}
        >
          <div>
            <div style={{marginBottom: '2mm'}}>
              <span style={{fontWeight: 'bold'}}>Patient Name:</span>{' '}
              {PrintUtil.toUpperCaseText(patientInfo?.name) || PrintUtil.toUpperCaseText(patientInfo?.patient_name)}
            </div>

            <div style={{marginBottom: '2mm'}}>
              <span style={{fontWeight: 'bold'}}>Age / Gender:</span>{' '}
              {PrintUtil.generateAgeGenderString(patientInfo)}
            </div>

            <div style={{marginBottom: '2mm'}}>
              <span style={{fontWeight: 'bold'}}>Phone:</span>{' '}
              {patientInfo?.phone || data?.patient_phone}
            </div>

            <div style={{marginBottom: '2mm'}}>
              <span style={{fontWeight: 'bold'}}>Date:</span>{' '}
              {DateUtil.formatDateForType(data?.date, 'date-only')}
            </div>
          </div>

          <div>
            <div style={{marginBottom: '2mm'}}>
              <span style={{fontWeight: 'bold'}}>Doctor Name:</span>{' '}
              Dr. {doctorInfo?.name || data?.doctor_name}
            </div>

            <div style={{marginBottom: '2mm'}}>
              <span style={{fontWeight: 'bold'}}>MRN:</span>{' '}
              {patientInfo?.mrn || data?.patient_mrn || 'N/A'}
            </div>

            <div style={{marginBottom: '2mm'}}>
              <span style={{fontWeight: 'bold'}}>Department:</span>{' '}
              {doctorInfo?.specialization || doctorInfo?.specialty || ''}
            </div>
          </div>
        </div>

        {data?.complaints?.some((c: any) => c.complaint?.trim()) && (
            <div style={{marginBottom: '4mm', fontSize: '11px'}}>
              <span style={{fontWeight: 'bold', marginRight: '5px'}}>Complaints:</span>
              <span>
            {data.complaints
            .filter((c: any) => c.complaint?.trim())
            .map((c: any) =>
                `${c.complaint.toUpperCase()}${
                    c.duration
                        ? c.duration.startsWith('since')
                            ? ` ${c.duration}`
                            : ` since ${c.duration}`
                        : ''
                }`
            )
            .join(' , ')}
          </span>
            </div>
        )}

        {data?.vitals && (data.vitals.bp || data.vitals.pulse || data.vitals.spo2) && (
            <div style={{marginBottom: '6mm', fontSize: '11px', fontWeight: 'bold'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                {data.vitals.bp && <div>BP {data.vitals.bp} mmHg |</div>}
                {data.vitals.pulse && <div>Pulse {data.vitals.pulse} bpm |</div>}
                {data.vitals.spo2 && <div>SPO2 {data.vitals.spo2} %</div>}
              </div>
            </div>
        )}

        {data?.medicines && data.medicines.some((m: any) => medicineNameOf(m)) && (
            <div style={{marginBottom: '8mm'}}>
              <div style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '4mm'}}>℞</div>

              <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '50% 12% 30% 8%',
                    borderBottom: '1px solid #000',
                    paddingBottom: '2mm',
                    marginBottom: '3mm',
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}
              >
                <div style={{textAlign: 'left'}}>Medicine</div>
                <div style={{textAlign: 'left'}}>Dosage</div>
                <div style={{textAlign: 'left'}}>Directions</div>
                <div style={{textAlign: 'center'}}>Qty</div>
              </div>

              {data.medicines
              .filter((m: any) => medicineNameOf(m))
              .map((med: any, index: number) => {
                const directions = buildDirectionsText(med);
                const frequencyText = buildDirectionsCell(med);
                const medicineName = medicineNameOf(med);

                return (
                    <div key={index} style={{marginBottom: '2mm', marginTop: '1mm', fontSize: '11px',  borderBottom: '1px solid #ddd'}}>
                      <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '50% 15% 30% 5%',
                            alignItems: 'center',
                            marginBottom: '1mm',
                          }}
                      >
                        <div style={{fontWeight: 'bold'}}>
                          <span style={{fontWeight: 'bold'}}>{index + 1})</span>{' '}
                          {medicineName.toUpperCase()}
                        </div>

                        <div style={{textAlign: 'left', fontWeight: 'bold'}}>
                          {normalizeText(med.dosage)}
                        </div>

                        <div style={{textAlign: 'left', fontWeight: '500'}}>
                          {hasText(frequencyText) ? frequencyText : ''}
                        </div>

                        <div style={{textAlign: 'center', fontWeight: 'bold'}}>
                          {med.quantity !== null && med.quantity !== undefined && med.quantity !== '' ? String(med.quantity) : ''}
                        </div>
                      </div>

                      {hasText(directions) && (
                          <div
                              style={{
                                marginLeft: '15px',
                                fontSize: '10px',
                                color: '#333',
                                marginBottom: '1mm'
                              }}
                          >
                            <span style={{fontWeight: 'bold', marginRight: '5px'}}>Timing :</span>
                            <span style={{color: '#0066cc', fontWeight: '500'}}>
                        {directions}
                      </span>
                          </div>
                      )}

                      {hasText(med.notes) && (
                          <div
                              style={{
                                marginLeft: '15px',
                                fontSize: '10px',
                                color: '#333'
                              }}
                          >
                            <span style={{fontWeight: 'bold', marginRight: '5px'}}>Notes :</span>
                            <span style={{color: '#0066cc', fontWeight: '500'}}>
                        {normalizeText(med.notes)}
                      </span>
                          </div>
                      )}
                    </div>
                );
              })}
            </div>
        )}

        {data?.investigations &&
            Array.isArray(data.investigations) &&
            data.investigations.some((i: any) =>
                (typeof i === 'string' && i.trim()) ||
                (i && typeof i.investigation === 'string' && i.investigation.trim())
            ) && (
                <div style={{marginBottom: '4mm', fontSize: '11px'}}>
                  <span style={{fontWeight: 'bold', marginRight: '5px'}}>{t.investigations}</span>
                  <span>
              {(data.investigations || [])
              .map((i: any) =>
                  typeof i === 'string'
                      ? i
                      : (i && typeof i.investigation === 'string' ? i.investigation : '')
              )
              .filter(Boolean)
              .join(', ')}
            </span>
                </div>
            )}

        {data?.next_visit?.date && (
            <div style={{marginBottom: '8mm', fontSize: '11px'}}>
              <span style={{fontWeight: 'bold', marginRight: '5px'}}>{t.nextVisit}</span>
              <span style={{color: '#0066cc', fontWeight: '500'}}>
            {DateUtil.formatDateForType(data.next_visit.date, 'date-only')}{' '}
                {data.next_visit.time ? `at ${DateUtil.formatDateForType(data.next_visit.time, 'time-only')}` : ''}
          </span>
            </div>
        )}

        {data?.advice && (
            <div style={{marginBottom: '8mm', fontSize: '11px'}}>
              <span style={{fontWeight: 'bold', marginRight: '5px'}}>{t.advice}</span>
              <div style={{
                color: '#0066cc',
                fontWeight: '500',
                marginTop: '2mm',
                lineHeight: '1.4'
              }}>
                {data.advice}
              </div>
            </div>
        )}

        <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '15mm',
              paddingTop: '10mm'
            }}
        >
          <div
              style={{
                textAlign: 'left',
                fontSize: '10px',
                fontWeight: 'bold',
                lineHeight: '1.2'
              }}
          >
            {settings?.profile?.signature ? (
                <div>
                  {settings.profile.signature.split('\n').map((line: string, index: number) => (
                      <span key={index}>
                  {line}
                        <br/>
                </span>
                  ))}
                </div>
            ) : (
                <>
                  {doctorInfo?.name && <div>Dr. {doctorInfo.name}</div>}
                  {clinicInfo?.name && <div>{clinicInfo.name}</div>}
                  {doctorInfo?.specialty && <div>{doctorInfo.specialty}</div>}
                </>
            )}
          </div>
        </div>

        <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '10mm',
              fontSize: '10px'
            }}
        >
          <div>
            <span style={{fontWeight: 'bold', marginBottom: '5mm'}}>Generated By </span>
            <span style={{padding: '3mm'}}>{currentUser?.name}</span>
          </div>

          <div style={{textAlign: 'right'}}>
            <div>
              <span style={{fontWeight: 'bold', marginBottom: '3mm'}}>Generated On </span>
              <span style={{padding: '3mm'}}>
              {DateUtil.formatDateForType(invoiceDate, 'date-time')}
            </span>
            </div>
          </div>
        </div>
      </>
  );
}